import React from 'react';
import { Link } from 'react-router-dom';
import { CalendarCheck, ArrowLeft, Shield, FileText, UserX, Scale, Mail } from 'lucide-react';

const CaliforniaPrivacyPage = () => {
  const lastUpdated = "March 20, 2026";
  const effectiveDate = "March 20, 2026";

  return (
    <div className="min-h-screen bg-paper">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-xl border-b border-ink-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <CalendarCheck className="w-7 h-7 text-ink" />
              <span className="text-xl font-bold text-ink font-display">Enota</span>
            </Link>
            <Link to="/" className="text-ink-600 hover:text-ink flex items-center gap-1 text-sm">
              <ArrowLeft className="w-4 h-4" />
              Back to home
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-md border border-ink-200 shadow-sm p-8 md:p-12">
            <h1 className="text-3xl sm:text-4xl font-bold text-ink font-display mb-2">
              California Privacy Notice
            </h1>
            <p className="text-ink-600 mb-2">Last Updated: {lastUpdated}</p>
            <p className="text-ink-600 mb-8">Effective Date: {effectiveDate}</p>

            {/* CCPA Rights Summary */}
            <div className="bg-amber-50 border border-amber-200 rounded-md p-6 mb-8">
              <h2 className="text-lg font-semibold text-amber-900 mb-4 flex items-center gap-2">
                <Scale className="w-5 h-5" />
                Your California Privacy Rights at a Glance
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <FileText className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-900">Right to Know</p>
                    <p className="text-sm text-amber-700">Request what data we collect about you</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <UserX className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-900">Right to Delete</p>
                    <p className="text-sm text-amber-700">Request deletion of your personal data</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-900">Right to Non-Discrimination</p>
                    <p className="text-sm text-amber-700">Equal service regardless of privacy choices</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-900">Right to Opt-Out</p>
                    <p className="text-sm text-amber-700">We do not sell your personal information</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="prose prose-slate max-w-none">
              <p className="text-ink-600 leading-relaxed mb-6">
                This California Privacy Notice supplements the information contained in our{' '}
                <Link to="/privacy" className="text-respondent hover:text-respondent underline">Privacy Policy</Link>{' '}
                and applies solely to visitors, users, and others who reside in the State of California 
                ("consumers" or "you"). Enota is a service owned and operated by <strong>OFFER GADGET LLC</strong>. 
                We adopt this notice to comply with the California Consumer Privacy Act of 2018 ("CCPA") as 
                amended by the California Privacy Rights Act of 2020 ("CPRA"), and any terms defined in the 
                CCPA/CPRA have the same meaning when used in this notice.
              </p>

              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4">
                1. Information We Collect
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                Enota collects information that identifies, relates to, describes, references, is 
                reasonably capable of being associated with, or could reasonably be linked, directly or 
                indirectly, with a particular consumer, household, or device ("personal information"). 
                In particular, we have collected the following categories of personal information from 
                consumers within the last twelve (12) months:
              </p>

              <div className="overflow-x-auto mb-6">
                <table className="w-full text-sm border border-ink-200 rounded-md">
                  <thead className="bg-paper">
                    <tr>
                      <th className="text-left py-3 px-4 text-ink font-semibold border-b border-ink-200">Category</th>
                      <th className="text-left py-3 px-4 text-ink font-semibold border-b border-ink-200">Examples</th>
                      <th className="text-left py-3 px-4 text-ink font-semibold border-b border-ink-200">Collected</th>
                    </tr>
                  </thead>
                  <tbody className="text-ink-600">
                    <tr className="border-b border-ink-200">
                      <td className="py-3 px-4 font-medium">A. Identifiers</td>
                      <td className="py-3 px-4">Real name, email address, phone number, unique personal identifier, online identifier, IP address, account name</td>
                      <td className="py-3 px-4 text-emerald-600 font-medium">YES</td>
                    </tr>
                    <tr className="border-b border-ink-200">
                      <td className="py-3 px-4 font-medium">B. Personal Information (Cal. Civ. Code § 1798.80(e))</td>
                      <td className="py-3 px-4">Name, address, telephone number, credit card number (via payment processor), bank account number (via payment processor)</td>
                      <td className="py-3 px-4 text-emerald-600 font-medium">YES</td>
                    </tr>
                    <tr className="border-b border-ink-200">
                      <td className="py-3 px-4 font-medium">C. Protected Classification Characteristics</td>
                      <td className="py-3 px-4">Age, race, color, ancestry, national origin, religion, marital status, sex, gender, sexual orientation, veteran status</td>
                      <td className="py-3 px-4 text-ink-600">NO</td>
                    </tr>
                    <tr className="border-b border-ink-200">
                      <td className="py-3 px-4 font-medium">D. Commercial Information</td>
                      <td className="py-3 px-4">Records of products or services purchased, purchasing histories, subscription plans</td>
                      <td className="py-3 px-4 text-emerald-600 font-medium">YES</td>
                    </tr>
                    <tr className="border-b border-ink-200">
                      <td className="py-3 px-4 font-medium">E. Biometric Information</td>
                      <td className="py-3 px-4">Genetic, physiological, behavioral characteristics, fingerprints, faceprints, voiceprints</td>
                      <td className="py-3 px-4 text-ink-600">NO</td>
                    </tr>
                    <tr className="border-b border-ink-200">
                      <td className="py-3 px-4 font-medium">F. Internet or Network Activity</td>
                      <td className="py-3 px-4">Browsing history, search history, information on interaction with our website, application, or advertisement</td>
                      <td className="py-3 px-4 text-emerald-600 font-medium">YES</td>
                    </tr>
                    <tr className="border-b border-ink-200">
                      <td className="py-3 px-4 font-medium">G. Geolocation Data</td>
                      <td className="py-3 px-4">Physical location (city/country level from IP address)</td>
                      <td className="py-3 px-4 text-emerald-600 font-medium">YES</td>
                    </tr>
                    <tr className="border-b border-ink-200">
                      <td className="py-3 px-4 font-medium">H. Sensory Data</td>
                      <td className="py-3 px-4">Audio, electronic, visual, thermal, olfactory, or similar information</td>
                      <td className="py-3 px-4 text-ink-600">NO</td>
                    </tr>
                    <tr className="border-b border-ink-200">
                      <td className="py-3 px-4 font-medium">I. Professional or Employment Information</td>
                      <td className="py-3 px-4">Current or past job history, performance evaluations</td>
                      <td className="py-3 px-4 text-ink-600">NO</td>
                    </tr>
                    <tr className="border-b border-ink-200">
                      <td className="py-3 px-4 font-medium">J. Education Information</td>
                      <td className="py-3 px-4">Education records, student records</td>
                      <td className="py-3 px-4 text-ink-600">NO</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4 font-medium">K. Inferences</td>
                      <td className="py-3 px-4">Profile reflecting preferences, characteristics, behavior, attitudes based on collected information</td>
                      <td className="py-3 px-4 text-emerald-600 font-medium">YES</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4">
                2. Sources of Personal Information
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                We obtain the categories of personal information listed above from the following sources:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-ink-600">
                <li><strong>Directly from you:</strong> Information you provide when creating an account, setting up renewals, updating preferences, or contacting support</li>
                <li><strong>Indirectly from you:</strong> Information collected automatically through your interaction with our Service, such as usage data and device information</li>
                <li><strong>From third parties:</strong> Information from service providers such as Google (for OAuth authentication), Stripe (for payment processing), and analytics providers</li>
              </ul>

              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4">
                3. Use of Personal Information
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                We may use or disclose the personal information we collect for one or more of the following 
                business purposes:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-ink-600">
                <li>To fulfill or meet the reason you provided the information (e.g., to send renewal reminders)</li>
                <li>To provide, support, personalize, and develop our Service</li>
                <li>To create, maintain, customize, and secure your account</li>
                <li>To process your requests, purchases, transactions, and payments</li>
                <li>To provide customer support and respond to your inquiries</li>
                <li>To personalize your experience and deliver relevant content and product offerings</li>
                <li>To maintain the safety, security, and integrity of our Service</li>
                <li>For testing, research, analysis, and product development</li>
                <li>To respond to law enforcement requests and comply with applicable law</li>
                <li>As described to you when collecting your personal information</li>
              </ul>

              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4">
                4. Sharing Personal Information
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                We may share your personal information with the following categories of third parties:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-ink-600">
                <li><strong>Service Providers:</strong> Companies that perform services on our behalf, such as payment processing (Stripe), email delivery (Resend), SMS services (Twilio), and cloud hosting (MongoDB Atlas)</li>
                <li><strong>Business Partners:</strong> Third parties with whom we partner to offer products or services</li>
                <li><strong>Affiliates:</strong> Our parent company, subsidiaries, and affiliates</li>
                <li><strong>Legal and Safety:</strong> Law enforcement, government agencies, or other parties when required by law or to protect our rights</li>
              </ul>

              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4">
                5. Sale of Personal Information
              </h2>
              <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4 mb-4">
                <p className="text-emerald-800 font-medium">
                  Enota does not sell personal information to third parties.
                </p>
              </div>
              <p className="text-ink-600 leading-relaxed mb-4">
                In the preceding twelve (12) months, we have not sold any personal information. We do not 
                have actual knowledge that we sell the personal information of consumers under 16 years of age.
              </p>

              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4">
                6. Your Rights Under the CCPA/CPRA
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                The CCPA/CPRA provides California residents with specific rights regarding their personal 
                information. This section describes your rights and explains how to exercise those rights.
              </p>

              <h3 className="text-lg font-semibold text-ink mt-6 mb-3">
                6.1 Right to Know and Data Portability
              </h3>
              <p className="text-ink-600 leading-relaxed mb-4">
                You have the right to request that we disclose certain information to you about our collection 
                and use of your personal information over the past 12 months. Once we receive and verify your 
                request, we will disclose to you:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-ink-600">
                <li>The categories of personal information we collected about you</li>
                <li>The categories of sources for the personal information we collected about you</li>
                <li>Our business or commercial purpose for collecting or selling that personal information</li>
                <li>The categories of third parties with whom we share that personal information</li>
                <li>The specific pieces of personal information we collected about you (data portability request)</li>
                <li>If we sold or disclosed your personal information for a business purpose, two separate lists identifying the categories of personal information sold and disclosed</li>
              </ul>

              <h3 className="text-lg font-semibold text-ink mt-6 mb-3">
                6.2 Right to Delete
              </h3>
              <p className="text-ink-600 leading-relaxed mb-4">
                You have the right to request that we delete any of your personal information that we collected 
                from you and retained, subject to certain exceptions. Once we receive and verify your request, 
                we will delete (and direct our service providers to delete) your personal information from our 
                records, unless an exception applies.
              </p>
              <p className="text-ink-600 leading-relaxed mb-4">
                We may deny your deletion request if retaining the information is necessary for us or our 
                service providers to:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-ink-600">
                <li>Complete the transaction for which we collected the personal information</li>
                <li>Provide a good or service that you requested</li>
                <li>Detect security incidents, protect against malicious, deceptive, fraudulent, or illegal activity</li>
                <li>Debug products to identify and repair errors</li>
                <li>Exercise free speech or ensure another consumer's right to exercise free speech</li>
                <li>Comply with the California Electronic Communications Privacy Act</li>
                <li>Comply with a legal obligation</li>
                <li>Make other internal and lawful uses of that information that are compatible with the context in which you provided it</li>
              </ul>

              <h3 className="text-lg font-semibold text-ink mt-6 mb-3">
                6.3 Right to Correct
              </h3>
              <p className="text-ink-600 leading-relaxed mb-4">
                You have the right to request that we correct inaccurate personal information that we maintain 
                about you. Upon receiving and verifying your request, we will use commercially reasonable efforts 
                to correct the inaccurate personal information.
              </p>

              <h3 className="text-lg font-semibold text-ink mt-6 mb-3">
                6.4 Right to Limit Use of Sensitive Personal Information
              </h3>
              <p className="text-ink-600 leading-relaxed mb-4">
                We do not collect or process sensitive personal information for purposes other than those 
                permitted by the CPRA. Therefore, we do not offer an opt-out for the use of sensitive 
                personal information.
              </p>

              <h3 className="text-lg font-semibold text-ink mt-6 mb-3">
                6.5 Right to Non-Discrimination
              </h3>
              <p className="text-ink-600 leading-relaxed mb-4">
                We will not discriminate against you for exercising any of your CCPA/CPRA rights. Unless 
                permitted by the CCPA/CPRA, we will not:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-ink-600">
                <li>Deny you goods or services</li>
                <li>Charge you different prices or rates for goods or services</li>
                <li>Provide you a different level or quality of goods or services</li>
                <li>Suggest that you may receive a different price or rate for goods or services or a different level or quality of goods or services</li>
              </ul>

              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4">
                7. Exercising Your Rights
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                To exercise the rights described above, please submit a verifiable consumer request to us by:
              </p>
              <div className="bg-paper p-4 rounded-md border border-ink-200 mb-4">
                <p className="text-ink mb-2"><strong>Email:</strong> support@renewalnote.com</p>
                <p className="text-ink mb-2"><strong>Subject Line:</strong> "California Privacy Rights Request"</p>
                <p className="text-ink"><strong>Mail:</strong> OFFER GADGET LLC, 53 Calle Palmeras Ste 601, San Juan, PR 00901</p>
              </div>
              <p className="text-ink-600 leading-relaxed mb-4">
                Only you, or a person you have authorized to act on your behalf, may make a verifiable consumer 
                request related to your personal information. You may also make a verifiable consumer request 
                on behalf of your minor child.
              </p>
              <p className="text-ink-600 leading-relaxed mb-4">
                You may only make a verifiable consumer request for access or data portability twice within a 
                12-month period. The verifiable consumer request must:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-ink-600">
                <li>Provide sufficient information that allows us to reasonably verify you are the person about whom we collected personal information or an authorized representative</li>
                <li>Describe your request with sufficient detail that allows us to properly understand, evaluate, and respond to it</li>
              </ul>

              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4">
                8. Verification Process
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                We cannot respond to your request or provide you with personal information if we cannot verify 
                your identity or authority to make the request and confirm the personal information relates to 
                you. Making a verifiable consumer request does not require you to create an account with us.
              </p>
              <p className="text-ink-600 leading-relaxed mb-4">
                We will only use personal information provided in a verifiable consumer request to verify the 
                requestor's identity or authority to make the request. For verification, we may ask you to:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-ink-600">
                <li>Confirm the email address associated with your account</li>
                <li>Provide information that matches information we have on file</li>
                <li>Answer security questions based on your account activity</li>
              </ul>

              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4">
                9. Response Timing and Format
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                We endeavor to respond to a verifiable consumer request within forty-five (45) days of its 
                receipt. If we require more time (up to an additional 45 days), we will inform you of the 
                reason and extension period in writing.
              </p>
              <p className="text-ink-600 leading-relaxed mb-4">
                If you have an account with us, we will deliver our written response to that account. If you 
                do not have an account with us, we will deliver our written response by mail or electronically, 
                at your option.
              </p>
              <p className="text-ink-600 leading-relaxed mb-4">
                Any disclosures we provide will only cover the 12-month period preceding the verifiable consumer 
                request's receipt. The response we provide will also explain the reasons we cannot comply with a 
                request, if applicable. For data portability requests, we will select a format to provide your 
                personal information that is readily useable and should allow you to transmit the information 
                from one entity to another entity without hindrance.
              </p>
              <p className="text-ink-600 leading-relaxed mb-4">
                We do not charge a fee to process or respond to your verifiable consumer request unless it is 
                excessive, repetitive, or manifestly unfounded. If we determine that the request warrants a fee, 
                we will tell you why we made that decision and provide you with a cost estimate before completing 
                your request.
              </p>

              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4">
                10. Authorized Agents
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                You may designate an authorized agent to make a request under the CCPA on your behalf. To do so, 
                you must:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-ink-600">
                <li>Provide the authorized agent with written permission to submit the request</li>
                <li>Verify your own identity directly with us</li>
                <li>Directly confirm with us that you provided the authorized agent permission to submit the request</li>
              </ul>
              <p className="text-ink-600 leading-relaxed mb-4">
                We may deny a request from an authorized agent that does not submit proof that they have been 
                authorized by you to act on your behalf.
              </p>

              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4">
                11. "Do Not Track" Signals
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                California law requires us to disclose how we respond to web browser "Do Not Track" signals. 
                We do not currently respond to "Do Not Track" signals because a uniform technological standard 
                has not yet been developed. We continue to review new technologies and may adopt a standard 
                once one is created.
              </p>

              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4">
                12. "Shine the Light" Law
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                California Civil Code Section 1798.83, also known as the "Shine The Light" law, permits users 
                who are California residents to request and obtain from us, once a year and free of charge, 
                information about categories of personal information (if any) we disclosed to third parties 
                for direct marketing purposes and the names and addresses of all third parties with which we 
                shared personal information in the immediately preceding calendar year.
              </p>
              <p className="text-ink-600 leading-relaxed mb-4">
                As stated above, we do not share personal information with third parties for their direct 
                marketing purposes.
              </p>

              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4">
                13. Minors Under 16 Years of Age
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                Our Service is not intended for anyone under 18 years of age. We do not knowingly collect 
                personal information from minors under 16. We do not sell the personal information of consumers 
                we know to be less than 16 years of age. If you are under 16, please do not use our Service or 
                provide any information to us.
              </p>

              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4">
                14. Changes to This Notice
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                We reserve the right to amend this California Privacy Notice at our discretion and at any time. 
                When we make changes to this notice, we will post the updated notice on our website and update 
                the notice's effective date. Your continued use of our Service following the posting of changes 
                constitutes your acceptance of such changes.
              </p>

              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4">
                15. Contact Information
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                If you have any questions or comments about this notice, our Privacy Policy, the ways in which 
                we collect and use your information, your choices and rights regarding such use, or wish to 
                exercise your rights under California law, please contact us at:
              </p>
              <div className="bg-paper p-4 rounded-md border border-ink-200 mb-4">
                <p className="text-ink font-bold mb-2">OFFER GADGET LLC</p>
                <p className="text-ink-600 mb-1">53 Calle Palmeras Ste 601</p>
                <p className="text-ink-600 mb-2">San Juan, PR 00901</p>
                <p className="text-ink-600 mb-1">Email: support@renewalnote.com</p>
                <p className="text-ink-600">Phone: (877) 739-2487</p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-ink text-ink-600/70 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <CalendarCheck className="w-6 h-6 text-white" />
              <span className="text-lg font-bold text-white font-display">Enota</span>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <Link to="/about" className="hover:text-white transition-colors">About</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Use</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/california-privacy" className="hover:text-white transition-colors">CA Privacy</Link>
              <Link to="/login" className="hover:text-white transition-colors">Login</Link>
            </div>
            <p className="text-sm">&copy; 2026 Enota. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CaliforniaPrivacyPage;
